import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "@stripe/stripe-js"

export default class extends Controller {
  static targets = ["mount", "wrapper", "error", "loading"]
  static values = {
    publicKey: String,
    onBehalfOf: String,
    amount: Number,
    currency: String,
    locale: String,
    createUrl: String,
    thanksUrl: String,
    errorMessage: String,
  }

  connect() {
    this.initStripe()
  }

  disconnect() {
    if (this.detailsToggleHandler) {
      this.mountTarget.closest("details")?.removeEventListener("toggle", this.detailsToggleHandler)
    }
    if (this.prewarmContainer) {
      this.prewarmContainer.remove()
    }
    if (this.expressCheckoutElement) {
      this.expressCheckoutElement.unmount()
    }
  }

  async initStripe() {
    this.stripe = await loadStripe(this.publicKeyValue)

    const elementsOptions = {
      mode: "payment",
      amount: this.amountValue,
      currency: this.currencyValue,
      locale: this.localeValue,
    }
    if (this.onBehalfOfValue) {
      elementsOptions.on_behalf_of = this.onBehalfOfValue
    }
    elementsOptions.appearance = {
      variables: {
        borderRadius: "9999px",
      },
    }
    this.elements = this.stripe.elements(elementsOptions)

    this.expressCheckoutElement = this.elements.create("expressCheckout", {
      emailRequired: true,
      paymentMethods: {
        applePay: "auto",
        googlePay: "auto",
        link: "never",
        paypal: "never",
        amazonPay: "never",
      },
      buttonType: {
        applePay: "plain",
        googlePay: "plain",
      },
      buttonHeight: 44,
    })

    this.expressCheckoutElement.on("ready", ({ availablePaymentMethods }) => {
      if (availablePaymentMethods) {
        this.walletsAvailable = true
      }
      this.walletCheckComplete = true
      this.revealIfVisible()
    })

    this.expressCheckoutElement.on("confirm", async (event) => {
      this.clearError()

      const { error: submitError } = await this.elements.submit()
      if (submitError) {
        this.showError(this.errorMessageValue)
        return
      }

      const response = await this.createPaymentIntent()
      if (!response) return

      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: response.client_secret,
        confirmParams: {
          return_url: this.thanksUrlValue,
        },
        redirect: "if_required",
      })

      if (error) {
        this.showError(this.errorMessageValue)
      } else {
        const piId = response.client_secret.split("_secret_")[0]
        const url = new URL(this.thanksUrlValue)
        url.searchParams.set("payment_intent", piId)
        window.location.href = url.toString()
      }
    })

    this.mountWithPrewarm()
  }

  mountWithPrewarm() {
    const details = this.mountTarget.closest("details")

    if (!details || details.open) {
      this.expressCheckoutElement.mount(this.mountTarget)
      return
    }

    this.prewarmContainer = document.createElement("div")
    Object.assign(this.prewarmContainer.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "1px",
      height: "1px",
      overflow: "hidden",
      clipPath: "inset(50%)",
    })
    document.body.appendChild(this.prewarmContainer)
    this.expressCheckoutElement.mount(this.prewarmContainer)

    this.detailsToggleHandler = () => {
      if (details.open) {
        this.expressCheckoutElement.unmount()
        this.prewarmContainer.remove()
        this.prewarmContainer = null
        this.expressCheckoutElement.mount(this.mountTarget)
        this.revealIfVisible()
        details.removeEventListener("toggle", this.detailsToggleHandler)
        this.detailsToggleHandler = null
      }
    }
    details.addEventListener("toggle", this.detailsToggleHandler)
  }

  revealIfVisible() {
    if (!this.walletCheckComplete) return
    if (this.revealing) return

    const details = this.mountTarget.closest("details")
    if (details && !details.open) return

    this.revealing = true

    if (!this.walletsAvailable) {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.remove("animate-pulse")
        this.loadingTarget.style.transition = "opacity 200ms ease-out"
        requestAnimationFrame(() => {
          if (this.hasLoadingTarget) {
            this.loadingTarget.style.opacity = "0"
          }
          setTimeout(() => this.element.remove(), 200)
        })
      } else {
        this.element.remove()
      }
      return
    }

    if (this.hasLoadingTarget) {
      this.loadingTarget.remove()
    }

    const expansionPanel = this.element.closest(".expansion-panel--expanded")
    if (expansionPanel) {
      expansionPanel.style.overflow = "hidden"
    }

    this.wrapperTarget.style.opacity = "0"
    this.wrapperTarget.classList.remove("hidden")

    requestAnimationFrame(() => {
      this.wrapperTarget.style.transition = "opacity 300ms ease-in"
      this.wrapperTarget.style.opacity = "1"

      if (expansionPanel) {
        expansionPanel.addEventListener(
          "transitionend",
          (e) => {
            if (e.target === expansionPanel && e.propertyName === "height") {
              expansionPanel.style.overflow = ""
            }
          },
          { once: true },
        )
      }

      setTimeout(() => {
        this.wrapperTarget.style.transition = ""
      }, 300)
    })
  }

  async createPaymentIntent() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "same-origin",
      })

      if (!response.ok) {
        this.showError(this.errorMessageValue)
        return null
      }

      return await response.json()
    } catch {
      this.showError(this.errorMessageValue)
      return null
    }
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
